import { Box, Button, Chip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { theme } from "@theme";
import type { ReactNode } from "react";

type OptionButtonProps = {
  label: string;
  description?: string;
  icon?: ReactNode;
  variant?: "card" | "horizontal" | "level";
  complete?: boolean;
  selected: boolean;
  onClick: () => void;
};

const optionButtonStyling = {
  display: "grid",
  gridTemplateRows: "auto 1fr",
  gap: "12px 0",
  padding: 2.25,
  border: `1px solid ${theme.palette.info.main}`,
  borderRadius: 2,
  color: "var(--text)",
  background: "var(--surface)",
  textAlign: "left",
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.03)",
  textTransform: "none",

  "&:hover": {
    borderColor: theme.palette.primary.main,
  },
};

const optionalButtonSelectedStyling = {
  borderColor: theme.palette.primary.main,
  background: theme.palette.secondary.light,
  boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
};

const optionalButtonHorizontalStyling = {
  gridTemplateColumns: "auto 1fr auto",
  gap: "0 12px",
  padding: 2,
};

const optionalButtonLevelStyling = {
  alignItems: "start",
  gap: 0,
};

export const OptionButton = ({
  label,
  description,
  icon,
  variant = "card",
  complete = false,
  selected,
  onClick,
}: OptionButtonProps) => {
  const buttonStyling = [
    optionButtonStyling,
    selected && optionalButtonSelectedStyling,
    variant === "horizontal" && optionalButtonHorizontalStyling,
    variant === "level" && optionalButtonLevelStyling,
  ];
  const completeChip = complete ? (
    <Chip
      label="Complete"
      size="small"
      className={variant === "horizontal" ? "option-complete-chip" : undefined}
      sx={{
        color: theme.palette.success.dark,
        backgroundColor: alpha(theme.palette.success.main, 0.12),
      }}
    />
  ) : null;

  return (
    <Button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      sx={buttonStyling}
    >
      {icon && (
        <Box
          sx={{
            display: "flex",
            color: theme.palette.info.dark,
          }}
          aria-hidden="true"
        >
          {icon}
        </Box>
      )}

      <Box sx={{ display: "grid", gap: variant === "level" ? 1.5 : 0.5 }}>
        {variant === "level" ? (
          <Box className="option-level-heading">
            <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>
              {label}
            </Typography>
            {completeChip}
          </Box>
        ) : (
          <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>
            {label}
          </Typography>
        )}
        <Typography variant="body1" sx={{ lineHeight: 1.25 }}>
          {description}
        </Typography>
      </Box>
      {variant !== "level" && completeChip}
      {selected && !complete && (
        <Box className="option-selected-mark" aria-hidden="true">
          ✓
        </Box>
      )}
    </Button>
  );
};
